import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { FileText, Newspaper, Calendar, ClipboardList, Users, Image } from 'lucide-react'
import { dashboardApi } from '../../services/api'
import { useAuth } from '../../context/AuthContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Card from '../../components/ui/Card'
import { staggerContainer, fadeInUp } from '../../utils/animations'
import { formatDate, formatDateTime } from '../../utils/date'
import { activityActionLabel, activityModuleLabel, activitySubjectLabel } from '../../utils/activityLog'

const STAT_CONFIG = [
  { key: 'pages', icon: FileText, color: 'bg-primary/10 text-primary', module: 'pages' },
  { key: 'news', icon: Newspaper, color: 'bg-secondary/10 text-secondary', module: 'news' },
  { key: 'events', icon: Calendar, color: 'bg-accent/10 text-accent', module: 'events' },
  { key: 'forms', icon: ClipboardList, color: 'bg-primary/10 text-primary', modules: ['forms_applications', 'forms_counseling', 'forms_contact'] },
  { key: 'users', icon: Users, color: 'bg-secondary/10 text-secondary', module: 'users' },
  { key: 'gallery', icon: Image, color: 'bg-accent/10 text-accent', module: 'gallery' },
]

function canShowSection(item, hasModuleAccess) {
  if (item.modules?.length) {
    return item.modules.some((module) => hasModuleAccess(module))
  }
  if (item.module) {
    return hasModuleAccess(item.module)
  }
  return false
}

export default function AdminDashboard() {
  const { t, i18n } = useTranslation()
  const { hasModuleAccess } = useAuth()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const visibleStats = useMemo(
    () => STAT_CONFIG.filter((item) => canShowSection(item, hasModuleAccess)),
    [hasModuleAccess],
  )

  const showLatestNews = hasModuleAccess('news')
  const showLatestEvents = hasModuleAccess('events')
  const showRecentForms =
    hasModuleAccess('forms_applications') ||
    hasModuleAccess('forms_counseling') ||
    hasModuleAccess('forms_contact')
  const showRecentActivity = hasModuleAccess('activity_logs')

  const recentFormItems = useMemo(() => {
    if (!data || !showRecentForms) return []

    const items = []

    if (hasModuleAccess('forms_applications')) {
      items.push(...(data.recent_applications || []))
    }
    if (hasModuleAccess('forms_counseling')) {
      items.push(...(data.recent_counseling || []))
    }
    if (hasModuleAccess('forms_contact')) {
      items.push(...(data.recent_contacts || []))
    }

    return items
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5)
  }, [data, hasModuleAccess, showRecentForms])

  useEffect(() => {
    dashboardApi
      .get()
      .then(({ data: response }) => setData(response.data || response))
      .catch(() => setData(null))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <LoadingSpinner className="py-20" />

  const stats = data?.stats || {}
  const isAr = i18n.language === 'ar'
  const pickTitle = (item) => (isAr ? item.title_ar || item.title_en : item.title_en || item.title_ar)
  const hasVisibleContent =
    visibleStats.length > 0 ||
    showLatestNews ||
    showLatestEvents ||
    showRecentForms ||
    showRecentActivity

  return (
    <div>
      <h1 className="mb-2 text-3xl font-bold text-primary">{t('admin.dashboard.title')}</h1>
      <p className="mb-8 text-text/60">{t('admin.dashboard.welcome')}</p>

      {!hasVisibleContent ? (
        <Card>
          <p className="text-sm text-text/60">{t('admin.dashboard.noAccess')}</p>
        </Card>
      ) : null}

      {visibleStats.length > 0 ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="mb-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-[repeat(auto-fit,minmax(180px,1fr))]"
        >
          {visibleStats.map(({ key, icon: Icon, color }) => (
            <motion.div key={key} variants={fadeInUp}>
              <Card>
                <div className="flex items-center gap-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-primary">{stats[key] ?? 0}</div>
                    <div className="text-sm text-text/60">{t(`admin.dashboard.stats.${key}`)}</div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-2">
        {showLatestNews ? (
          <Card>
            <h2 className="mb-4 text-lg font-semibold text-primary">{t('admin.dashboard.latestNews')}</h2>
            <div className="space-y-3">
              {(data?.latest_news || []).length > 0 ? (
                (data?.latest_news || []).map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-4 border-b border-border/60 pb-3 last:border-0">
                    <span className="font-medium text-text">{pickTitle(item)}</span>
                    <span className="text-sm text-text/50">{formatDate(item.published_at)}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-text/50">{t('admin.table.noData')}</p>
              )}
            </div>
          </Card>
        ) : null}

        {showLatestEvents ? (
          <Card>
            <h2 className="mb-4 text-lg font-semibold text-primary">{t('admin.dashboard.latestEvents')}</h2>
            <div className="space-y-3">
              {(data?.latest_events || []).length > 0 ? (
                (data?.latest_events || []).map((item) => (
                  <div key={item.id} className="flex items-center justify-between gap-4 border-b border-border/60 pb-3 last:border-0">
                    <span className="font-medium text-text">{pickTitle(item)}</span>
                    <span className="text-sm text-text/50">{formatDate(item.event_date)}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-text/50">{t('admin.table.noData')}</p>
              )}
            </div>
          </Card>
        ) : null}

        {showRecentForms ? (
          <Card>
            <h2 className="mb-4 text-lg font-semibold text-primary">{t('admin.dashboard.recentForms')}</h2>
            <div className="space-y-3">
              {recentFormItems.length > 0 ? (
                recentFormItems.map((item) => (
                  <div key={`${item.id}-${item.email}`} className="flex items-center justify-between gap-4 border-b border-border/60 pb-3 last:border-0">
                    <span className="font-medium text-text">{item.parent_name || item.name}</span>
                    <span className="text-sm text-text/50">{formatDateTime(item.created_at)}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-text/50">{t('admin.table.noData')}</p>
              )}
            </div>
          </Card>
        ) : null}

        {showRecentActivity ? (
          <Card>
            <h2 className="mb-4 text-lg font-semibold text-primary">{t('admin.dashboard.recentActivity')}</h2>
            <div className="space-y-3">
              {(data?.recent_activity || []).length > 0 ? (
                (data?.recent_activity || []).map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 border-b border-border/60 pb-3 last:border-0"
                  >
                    <div className="min-w-0 text-start">
                      <div className="font-medium text-text">{activityModuleLabel(item.module, t)}</div>
                      <div className="mt-0.5 text-sm text-text/60">
                        {activityActionLabel(item.action, t)}
                        {activitySubjectLabel(item) ? (
                          <>
                            <span className="mx-1.5 text-text/30">·</span>
                            <span className="text-text/80">{activitySubjectLabel(item)}</span>
                          </>
                        ) : null}
                        <span className="mx-1.5 text-text/30">·</span>
                        {item.user?.name || item.user_name || t('admin.activity.unknownUser')}
                      </div>
                    </div>
                    <div className="shrink-0 text-sm text-text/50">{formatDateTime(item.created_at)}</div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-text/50">{t('admin.table.noData')}</p>
              )}
            </div>
          </Card>
        ) : null}
      </div>
    </div>
  )
}
